import { State } from 'src/replicant/State';
import { leaderboardReward } from './leagues.ruleset';
import { getMatch2MaxLevel } from '../match2.getters';
import { leaguePhase2Allowlist } from './leaguePhase2Allowlist';

/** Difficulty cohorts used by the retired Masters League A/B test. */
export type MastersLeagueDifficultyBucket = 'control' | 'enabled';

/** First league season created after A/B 0483 resolved to enabled. */
const mastersLeagueDifficultyEnabledFromLeagueId = 4750;

/** Retired A/B 0483 assignment retained in player ruleset state. */
const mastersLeagueDifficultyTestId = '0483_masters_league_difficulty_phase_1';

/**
 * Get current league id.
 * @returns
 */
export function getCurrentLeagueId(state: State): string {
  return `${getMatch2MaxLevel(state)}`;
}

/**
 * Check if player can play in league.
 * @param state
 * @returns
 */
export function canPlayLeague(state: State): boolean {
  // Check if player has beat max level.
  return state.match2.level > getMatch2MaxLevel(state);
}

/**
 * Check if player can see leagues.
 * @param state
 * @returns
 */
export function canSeeLeague(state: State): boolean {
  return canPlayLeague(state);
}

/**
 * Check if player has joined league.
 * @param state
 * @param leagueId
 * @returns
 */
export function hasJoinedLeague(
  state: State,
  leagueId: string = getCurrentLeagueId(state),
): boolean {
  return !!state.match2.leagues[leagueId];
}

/**
 * Resolve the retired A/B 0483 assignment for an old league entry.
 *
 * @param state Player state containing historical ruleset assignments.
 * @returns The historical difficulty bucket, defaulting to control.
 */
function getLegacyMastersLeagueDifficultyBucket(
  state: Pick<State, 'ruleset'>,
): MastersLeagueDifficultyBucket {
  // Read the retained assignment because some players joined before their bucket was persisted.
  const legacyAssignments = state.ruleset?.abTests as
    | Record<string, { bucketId?: string }>
    | undefined;
  const bucket = legacyAssignments?.[mastersLeagueDifficultyTestId]?.bucketId;

  // Preserve the enabled assignment when it was retained in ruleset state.
  if (bucket === 'enabled') return 'enabled';

  // Preserve the original control fallback for missing or unknown historical assignments.
  return 'control';
}

/**
 * Get the difficulty bucket used to rank a league entry.
 *
 * @param state Player state containing league entries and historical assignments.
 * @param leagueId League season to rank.
 * @returns The bucket used by the global league scoreboard.
 */
export function getMastersLeagueDifficultyBucketForLeague(
  state: State,
  leagueId: string = getCurrentLeagueId(state),
): MastersLeagueDifficultyBucket {
  // Use the snapshot taken when the player joined the historical league.
  const persistedBucket =
    state.match2.leagues[leagueId]?.mastersLeagueDifficultyBucket;
  if (persistedBucket === 'enabled' || persistedBucket === 'control') {
    return persistedBucket;
  }

  // All leagues introduced after the level 4750 release use the enabled behavior.
  if (Number(leagueId) >= mastersLeagueDifficultyEnabledFromLeagueId) {
    return 'enabled';
  }

  // Recover the historical assignment for old entries that lack a persisted snapshot.
  return getLegacyMastersLeagueDifficultyBucket(state);
}

/**
 * Get the player's score for a league.
 * @param state
 * @param leagueId
 * @returns The score, or undefined if the player has no entry for the league.
 */
export function getLeagueScore(
  state: State,
  leagueId: string = getCurrentLeagueId(state),
): number | undefined {
  return state.match2.leagues[leagueId]?.score;
}

/**
 * Get leagues that have ended, but need a final rank.
 * @param state
 * @returns
 */
export function getEndedLeagues(state: State): string[] {
  return Object.keys(state.match2.leagues).filter((id) => {
    return (
      id !== getCurrentLeagueId(state) &&
      !state.match2.leagues[id].finalRank &&
      !state.match2.leagues[id].claimed
    );
  });
}

/**
 * Get leagues that are ready to be claimed.
 * @param state
 * @returns
 */
export function getUnclaimedLeagues(state: State): string[] {
  return Object.keys(state.match2.leagues).filter((id) => {
    return (
      id !== getCurrentLeagueId(state) &&
      state.match2.leagues[id].finalRank &&
      !state.match2.leagues[id].claimed
    );
  });
}

/**
 * Get reward for league.
 * @param finalRank
 * @returns
 */
export function getLeagueReward(finalRank: number) {
  return leaderboardReward.find((reward) => finalRank <= reward.rangeEnd)
    ?.reward;
}

/**
 * Get the level number the player is on in the league.
 * @param state
 * @returns
 */
export function getLeagueLevel(state: State): number {
  return resolveLeagueLevel(state, 0);
}

/**
 * Get the level number the player will be served after their next league win,
 * so its region assets can be preloaded.
 * @param state
 * @returns
 */
export function getNextLeagueLevel(state: State): number {
  return resolveLeagueLevel(state, 1);
}

/**
 * Resolve the league level `lookahead` wins ahead of the player's current
 * position. The curated allow-list is indexed by a cursor that persists
 * across league cycles (#11743).
 * @param state
 * @param lookahead
 * @returns
 */
function resolveLeagueLevel(state: State, lookahead: number): number {
  // Cycle through the curated levels in sheet order without reshuffling.
  const index = state.match2.leaguePhaseCursor + lookahead;

  // Return the current position, wrapping after the last curated level.
  return leaguePhase2Allowlist[index % leaguePhase2Allowlist.length];
}

/**
 * Get current phase of league.
 * @param state
 * @returns
 */
export function getCurrentLeaguePhase(state: State): number {
  const eventId = getCurrentLeagueId(state);
  const score = state.match2.leagues[eventId]?.score ?? 0;
  return score + 1;
}
